/**
 * The member menu (user-management §7.2 C8): one builder for Hub's member
 * list, profile card and voice rows, so every place offers the same
 * actions. Every item is permission-gated with `memberActions`;
 * people above you show one disabled line saying why (`mod.cantHigher`).
 */
import { RelationshipType, type Snowflake } from '@neosocial/protocol';
import type { CSSProperties } from 'react';
import { tr, useI18n } from '../i18n';
import { api } from '../lib/api';
import { useData } from '../store/data';
import { isDeletedUser, roleColorPair, userName } from '../store/selectors';
import { Avatar } from '../ui/kit';
import { useUm } from '../store/um';
import { leaveVoice } from '../voice/controller';
import { useVoice } from '../voice/store';
import {
  confirmDialog,
  openMenu,
  toast,
  type Anchor,
  type LiveMenuItems,
  type MenuItem,
} from '../ui/overlay';
import { runAction } from './actions';
import { openModDialog } from './ModDialogs';
import { assignableRoles, memberActions, POKE_BLOCK_KEYS } from './perms';

/**
 * Items that follow live state (`LiveMenuItems`): rebuilt only when `key`
 * (everything `build` reads, as one string) changes, looked at whenever one
 * of `sources` says something changed.
 */
function liveItems(
  key: () => string,
  build: () => MenuItem[],
  sources: Array<(onChange: () => void) => () => void>,
): LiveMenuItems {
  let k = key();
  let items = build();
  return {
    subscribe(onChange) {
      const offs = sources.map((s) => s(onChange));
      return () => offs.forEach((off) => off());
    },
    get() {
      const now = key();
      if (now !== k) {
        k = now;
        items = build();
      }
      return items;
    },
  };
}

/**
 * Gives a member a role or takes it away (Discord's member Roles submenu and
 * the profile's roles). The server runs it through the member PATCH
 * (`MembersService.modify`): the same checks, one MEMBER_ROLE_UPDATE entry
 * "via the app", and GUILD_MEMBER_UPDATE to everyone, which is what turns
 * the checkbox and the profile's role chips. One role per call, so a role
 * another moderator gave a moment ago is never taken away by a stale list.
 */
export async function setMemberRole(
  guildId: Snowflake,
  userId: Snowflake,
  roleId: Snowflake,
  on: boolean,
): Promise<boolean> {
  const r = await runAction(() =>
    on
      ? api.um.addMemberRole(guildId, userId, roleId)
      : api.um.removeMemberRole(guildId, userId, roleId),
  );
  return r.ok;
}

/** A role's colour dot in a menu item, in its theme pair (`.nx-rc`). */
function RoleDot({ color }: { color: number }) {
  const [l, dk] = roleColorPair(color || 0x99aab5);
  return (
    <i
      className="nx-rc ns-mi-rc"
      style={{ '--rc-l': l, '--rc-d': dk } as CSSProperties}
      aria-hidden="true"
    />
  );
}

/**
 * The Roles submenu of a member's menu (Discord's): a checkbox for each role
 * you can give (`assignableRoles`), ticked while the member has it. A click
 * gives or takes the role and leaves the submenu open; the tick follows the
 * member's roles as the gateway reports them.
 */
export function memberRoleItems(guildId: Snowflake, userId: Snowflake): MenuItem[] {
  const d = useData.getState();
  const has = d.members[guildId]?.[userId]?.roles ?? [];
  return assignableRoles(d, guildId).map((r) => {
    const on = has.includes(r.id);
    return {
      id: 'role-' + r.id,
      label: r.name,
      iconNode: <RoleDot color={r.color} />,
      checked: on,
      toggle: true,
      keepOpen: true,
      run: () => void setMemberRole(guildId, userId, r.id, !on),
    };
  });
}

/** `memberRoleItems`, live: an open submenu follows role and member changes made anywhere. */
export function liveMemberRoles(guildId: Snowflake, userId: Snowflake): LiveMenuItems {
  return liveItems(
    () => {
      const d = useData.getState();
      return JSON.stringify([
        assignableRoles(d, guildId).map((r) => [r.id, r.name, r.color]),
        d.members[guildId]?.[userId]?.roles ?? [],
      ]);
    },
    () => memberRoleItems(guildId, userId),
    [useData.subscribe],
  );
}

export async function ignoreUser(userId: Snowflake, name: string): Promise<void> {
  const { t } = tr();
  // "You stay friends" only to a friend
  const friend = useData.getState().relationships[userId]?.type === RelationshipType.FRIEND;
  const ok = await confirmDialog({
    title: t('ignore.confirm.title', { name }),
    body: t(friend ? 'ignore.confirm.body' : 'social.ignore.bodyNotFriend'),
    confirmLabel: t('ignore.action'),
    icon: 'eye',
  });
  if (!ok) return;
  const r = await runAction(() => api.um.ignore(userId));
  if (!r.ok) return;
  useUm.setState((s) => ({ ignored: { ...s.ignored, [userId]: true } }));
  toast(t('um.ignore.on', { name }), { icon: 'eye' });
}

export async function unignoreUser(userId: Snowflake, name: string): Promise<void> {
  const r = await runAction(() => api.um.unignore(userId));
  if (!r.ok) return;
  useUm.setState((s) => {
    const ignored = { ...s.ignored };
    delete ignored[userId];
    return { ignored };
  });
  toast(tr().t('um.ignore.off', { name }), { icon: 'eye' });
}

async function voiceFlag(
  guildId: Snowflake,
  userId: Snowflake,
  body: { mute?: boolean; deaf?: boolean; channel_id?: null },
  doneKey: string,
): Promise<void> {
  const name = userName(useData.getState(), userId, guildId);
  const r = await runAction(() => api.um.modifyMember(guildId, userId, body));
  if (r.ok) toast(tr().t(doneKey, { name }), { icon: 'check', tone: 'ok' });
}

/** This client's own voice session is in `channelId` (connecting or connected). */
function myVoiceIn(channelId: Snowflake | null): boolean {
  const s = useVoice.getState().session;
  return !!channelId && s.channelId === channelId && s.status !== 'idle' && s.status !== 'failed';
}

export type ServerFlag = 'mute' | 'deaf';

/**
 * The label of the server mute / deafen action: the undo ("Server unmute",
 * "Server undeafen") while it is on. Every screen that offers it uses this
 * (live bug 2026-10-28: the label never turned into the undo).
 *
 * One pattern everywhere: the label says what a click does, so the item is
 * a plain menu item or button, never also checked or pressed ("Server
 * unmute" with a check mark read as "unmute is on"; a toggle's label must
 * not change with its state, WAI-ARIA). The same as "Mute for me" /
 * "Unmute for me" and "Time out" / "Remove timeout".
 */
export function serverFlagLabelKey(flag: ServerFlag, active: boolean): string {
  if (flag === 'mute') return active ? 'um.menu.unmute' : 'mod.serverMute';
  return active ? 'um.menu.undeafen' : 'mod.serverDeafen';
}

/** A moderator's server mute or deafen (or its undo), from any screen. */
export function setServerFlag(
  guildId: Snowflake,
  userId: Snowflake,
  flag: ServerFlag,
  on: boolean,
): Promise<void> {
  const done =
    flag === 'mute'
      ? on
        ? 'um.mute.done'
        : 'um.mute.undone'
      : on
        ? 'um.deaf.done'
        : 'um.deaf.undone';
  return voiceFlag(guildId, userId, flag === 'mute' ? { mute: on } : { deaf: on }, done);
}

/**
 * The label of the channel mute / deafen action (2026-10-28): the undo
 * ("Channel unmute", "Channel undeafen") while it is on, the same pattern as
 * `serverFlagLabelKey` (a plain item whose label says what a click does).
 */
export function channelFlagLabelKey(flag: ServerFlag, active: boolean): string {
  if (flag === 'mute') return active ? 'um.menu.channelUnmute' : 'um.menu.channelMute';
  return active ? 'um.menu.channelUndeafen' : 'um.menu.channelDeafen';
}

/**
 * A moderator's channel mute or deafen in one voice channel (or its undo),
 * from any screen: the member menus (the channel the person is in now) and
 * the channel's "Muted in this channel" list (wherever they are).
 */
export async function setChannelFlag(
  channelId: Snowflake,
  userId: Snowflake,
  flag: ServerFlag,
  on: boolean,
): Promise<boolean> {
  const d = useData.getState();
  const guildId = d.channels[channelId]?.guild_id ?? null;
  const name = userName(d, userId, guildId ?? undefined);
  const channel = d.channels[channelId]?.name ?? '';
  const r = await runAction(() =>
    api.um.setChannelVoiceMute(channelId, userId, flag === 'mute' ? { mute: on } : { deaf: on }),
  );
  if (r.ok) {
    const done =
      flag === 'mute'
        ? on
          ? 'um.chmute.done'
          : 'um.chmute.undone'
        : on
          ? 'um.chdeaf.done'
          : 'um.chdeaf.undone';
    toast(tr().t(done, { name, channel }), { icon: 'check', tone: 'ok' });
  }
  return r.ok;
}

/**
 * The menu items for a member. `social` adds Ignore (the profile card has
 * its own Block and friend items).
 */
export function memberMenuItems(
  guildId: Snowflake | null,
  userId: Snowflake,
  o: { social?: boolean } = {},
): MenuItem[] {
  const { t, fmt } = tr();
  const d = useData.getState();
  if (!guildId || !d.me) return [];
  const a = memberActions(d, guildId, userId);
  const name = userName(d, userId, guildId);
  const open = (kind: Parameters<typeof openModDialog>[0]['kind']) => () =>
    openModDialog({ kind, guildId, userId });
  const items: MenuItem[] = [];
  if (a.nick)
    items.push({
      label: t(a.isMe ? 'um.menu.nickMine' : 'um.menu.nick'),
      icon: 'edit',
      run: open('nick'),
    });
  if (a.poke) items.push({ label: t('um.menu.poke'), icon: 'zap', run: open('poke') });
  // offered but not sendable now (you invisible or out of voice, they
  // offline): shown disabled with the server's reason (2026-10-29)
  else if (a.pokeBlocked)
    items.push({
      label: t('um.menu.poke'),
      icon: 'zap',
      disabled: true,
      sub: t(POKE_BLOCK_KEYS[a.pokeBlocked]),
    });
  if (o.social && !a.isMe) {
    const ignored = !!useUm.getState().ignored[userId];
    items.push(
      ignored
        ? { label: t('ignore.undo'), icon: 'eye', run: () => void unignoreUser(userId, name) }
        : { label: t('ignore.action'), icon: 'eye', run: () => void ignoreUser(userId, name) },
    );
  }
  // Discord's "Roles ›": give or take any role below your highest one
  if (a.roles)
    items.push({
      id: 'roles',
      label: t('um.menu.roles'),
      icon: 'tag',
      submenu: () => liveMemberRoles(guildId, userId),
    });

  const mod: MenuItem[] = [];
  // in voice: both, each with its undo; out of voice (the flag outlasts a
  // leave) `memberActions` allows only the undo, so nobody stays muted
  // until they rejoin (QA 2026-10-28 round 2)
  if (a.mute)
    mod.push({
      id: 'server-mute',
      label: t(serverFlagLabelKey('mute', a.serverMuted)),
      icon: a.serverMuted ? 'mic' : 'mic-off-mod',
      run: () => void setServerFlag(guildId, userId, 'mute', !a.serverMuted),
    });
  if (a.deafen)
    mod.push({
      id: 'server-deaf',
      label: t(serverFlagLabelKey('deaf', a.serverDeafened)),
      icon: a.serverDeafened ? 'headphones' : 'headphones-off-mod',
      run: () => void setServerFlag(guildId, userId, 'deaf', !a.serverDeafened),
    });
  // the channel they are in now (2026-10-28): each with its undo, live; out
  // of that channel it is lifted from the channel's settings
  const vcId = a.voiceChannelId;
  const vcName = vcId ? (d.channels[vcId]?.name ?? '') : '';
  if (vcId && a.channelMute)
    mod.push({
      id: 'channel-mute',
      label: t(channelFlagLabelKey('mute', a.channelMuted)),
      sub: vcName,
      icon: a.channelMuted ? 'mic' : 'mic-off-ch',
      run: () => void setChannelFlag(vcId, userId, 'mute', !a.channelMuted),
    });
  if (vcId && a.channelDeafen)
    mod.push({
      id: 'channel-deaf',
      label: t(channelFlagLabelKey('deaf', a.channelDeafened)),
      sub: vcName,
      icon: a.channelDeafened ? 'headphones' : 'headphones-off-ch',
      run: () => void setChannelFlag(vcId, userId, 'deaf', !a.channelDeafened),
    });
  if (a.voiceChannelId) {
    if (a.move) mod.push({ label: t('mod.move'), icon: 'cm-move', run: open('move') });
    if (a.disconnect)
      mod.push({
        label: t('mod.disconnect'),
        icon: 'phone-off',
        // yourself, connected here: a plain leave (the server's disconnect
        // would tell you "a moderator disconnected you"); in voice
        // elsewhere (another device), the server's disconnect, as Discord's
        run: () =>
          a.isMe && myVoiceIn(a.voiceChannelId)
            ? leaveVoice()
            : void voiceFlag(guildId, userId, { channel_id: null }, 'um.disconnect.done'),
      });
  }
  if (a.timeout)
    mod.push(
      a.timedOutUntil
        ? {
            label: t('um.menu.untimeout'),
            sub: t('um.timeout.badge', { time: fmt.stamp(a.timedOutUntil) }),
            icon: 'clock',
            run: () =>
              void runAction(() =>
                api.um.modifyMember(guildId, userId, { communication_disabled_until: null }),
              ).then(
                (r) =>
                  r.ok && toast(t('um.timeout.lifted', { name }), { icon: 'clock', tone: 'ok' }),
              ),
          }
        : { label: t('um.menu.timeout'), icon: 'clock', run: open('timeout') },
    );
  if (a.kick)
    mod.push({ label: t('um.menu.kick'), icon: 'arrow-out', danger: true, run: open('kick') });
  if (a.ban) mod.push({ label: t('um.menu.ban'), icon: 'cm-ban', danger: true, run: open('ban') });
  if (a.transfer)
    mod.push({ label: t('owner.transfer'), icon: 'crown', danger: true, run: open('transfer') });

  if (mod.length || a.blockedByHierarchy) {
    if (items.length) items.push({ sep: true });
    items.push({ header: t('um.menu.mod') });
    if (mod.length) items.push(...mod);
    else
      items.push({
        label: a.targetIsOwner ? t('um.menu.owner') : t('mod.cantHigher'),
        icon: 'lock',
        disabled: true,
      });
  }
  return items;
}

/**
 * Everything a member's menu is built from, as one string: the member's
 * actions (with their server mute and deafen), their name, Ignore and the
 * language.
 */
function memberMenuKey(guildId: Snowflake | null, userId: Snowflake): string {
  const d = useData.getState();
  if (!guildId || !d.me) return '';
  const a = memberActions(d, guildId, userId);
  return JSON.stringify([
    a,
    // the channel items name the channel they are for
    a.voiceChannelId ? (d.channels[a.voiceChannelId]?.name ?? '') : '',
    userName(d, userId, guildId),
    !!useUm.getState().ignored[userId],
    useI18n.getState().lang,
  ]);
}

/**
 * A place's own items in a member's menu that follow live state too (the
 * voice tile's Volume and "Mute for me", which hold only while the person
 * is in your voice channel): an open menu rebuilds them whenever `key`
 * changes, looked at whenever `subscribe` says something changed. QA
 * 2026-10-28 round 3: a tile's open menu kept Volume and "Mute for me"
 * after the person left voice.
 */
export interface LiveExtra {
  /** The items as they are now (built with the current language). */
  build(): MenuItem[];
  /** Items after the member's own, last (Discord's Copy User ID); built as `build` is. */
  tail?(): MenuItem[];
  /** Everything `build` reads, as one string. */
  key(): string;
  subscribe(onChange: () => void): () => void;
}

/**
 * A member's menu items, live: an open menu rebuilds them whenever
 * anything they are built from changes, so its labels and clicks always
 * follow the person's current state. QA 2026-10-28 round 2: a menu opened
 * before another moderator muted the person still said "Server mute", and
 * its click sent the mute again. `compose` adds the place's own items;
 * `extra` says when those change (`LiveExtra`).
 */
export function liveMemberMenu(
  guildId: Snowflake | null,
  userId: Snowflake,
  compose: (own: MenuItem[]) => MenuItem[],
  extra?: Pick<LiveExtra, 'key' | 'subscribe'>,
): LiveMenuItems {
  return liveItems(
    () => memberMenuKey(guildId, userId) + (extra ? '\n' + extra.key() : ''),
    () => compose(memberMenuItems(guildId, userId, { social: true })),
    [
      useData.subscribe,
      useUm.subscribe,
      useI18n.subscribe,
      ...(extra ? [(onChange: () => void) => extra.subscribe(onChange)] : []),
    ],
  );
}

/**
 * Right-click on a member anywhere in Hub. `extra`: the place's own items,
 * first; fixed, or live (`LiveExtra`) when they depend on more than the
 * member (the voice tile's).
 */
export function openMemberMenu(
  anchor: Anchor,
  guildId: Snowflake | null,
  userId: Snowflake,
  extra: MenuItem[] | LiveExtra = [],
): void {
  const liveExtra = Array.isArray(extra) ? undefined : extra;
  const extras = () => (Array.isArray(extra) ? extra : extra.build());
  const tail = () => liveExtra?.tail?.() ?? [];
  const live = liveMemberMenu(
    guildId,
    userId,
    (own) => {
      // the place's items, the member's, then the place's last ones, a line between
      const groups = [extras(), own, tail()].filter((g) => g.length);
      return groups
        .flatMap((g, i): MenuItem[] => (i ? [{ sep: true }, ...g] : g))
        .filter((it, i, all) => !(it.sep && (i === 0 || i === all.length - 1 || all[i - 1]?.sep)));
    },
    liveExtra,
  );
  if (!live.get().length) return;
  const name = userName(useData.getState(), userId, guildId);
  openMenu(anchor, live, {
    label: name,
    // a phone's action sheet covers the tile or row it came from: it says whose it is (V-11)
    sheetHead: <MemberSheetHead guildId={guildId} userId={userId} />,
  });
}

/** Whose action sheet it is: their picture, name and username (Discord's mobile user sheet). */
function MemberSheetHead({ guildId, userId }: { guildId: Snowflake | null; userId: Snowflake }) {
  const user = useData((d) => d.users[userId]);
  const name = useData((d) => userName(d, userId, guildId));
  return (
    <>
      <Avatar user={user} name={name} size={40} />
      <span className="nx-sheet-who-t">
        <b dir="auto">{name}</b>
        {user && !isDeletedUser(user) && user.username !== name ? (
          <small dir="ltr">{user.username}</small>
        ) : null}
      </span>
    </>
  );
}
